import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, getBaseUrl } from '../../utils/seo';

const CompanyPage = () => {
  // SEO設定
  useSEO({
    title: '会社概要｜おかやま解体工事株式会社 - 岡山市の解体・造園工事',
    description: 'おかやま解体工事株式会社の会社概要。1993年創業、岡山市北区に本社を置き、建物解体工事、造園工事など総合建設サービスを提供。従業員10名、創業30年以上の実績と信頼。',
    keywords: '会社概要,おかやま解体工事株式会社,岡山市北区,建設業,解体工事会社,造園工事会社',
    canonicalPath: '/company',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'おかやま解体工事株式会社',
      url: getBaseUrl(),
      logo: `${getBaseUrl()}/logo.png`,
      foundingDate: '1993',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '北区○○町1-2-3',
        addressLocality: '岡山市',
        addressRegion: '岡山県',
        postalCode: '700-0000',
        addressCountry: 'JP'
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+81-86-012-3456',
        contactType: 'customer service',
        areaServed: 'JP',
        availableLanguage: 'Japanese'
      },
      founder: {
        '@type': 'Person',
        name: '岡山太郎'
      },
      numberOfEmployees: 10
    }
  });

  const companyInfo = [
    { label: '会社名', value: 'おかやま解体工事株式会社' },
    { label: '代表取締役', value: '岡山太郎' },
    { label: '所在地', value: '〒700-0000 岡山市北区○○町1-2-3' },
    { label: '電話番号', value: '086-012-3456' },
    { label: 'FAX', value: '086-012-3457' },
    { label: '設立', value: '1993年' },
    { label: '従業員数', value: '10名' },
    { label: '営業時間', value: '8:00 〜 17:00' },
    { label: '定休日', value: '日曜日・祝日' },
  ];

  const certifications = [
    {
      icon: 'ri-shield-check-line',
      title: '建設業許可',
      description: '岡山県知事許可（般-○○）第○○○○○号',
    },
    {
      icon: 'ri-recycle-line',
      title: '産業廃棄物収集運搬業許可',
      description: '岡山県許可 第○○○○○○○号',
    },
    {
      icon: 'ri-plant-line',
      title: '造園工事業登録',
      description: '岡山県登録 第○○○○号',
    },
    {
      icon: 'ri-team-line',
      title: '建設業労働災害防止協会',
      description: '会員事業所',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[300px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/d49417bcf32190a0af62da65f0ed0e4a.jpeg"
            alt="会社概要"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-4 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full mb-6">
            <i className="ri-building-line text-white w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white">創業30年以上の実績</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
            会社概要
          </h1>
          <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto">
            地域に根ざした信頼と実績
          </p>
        </div>
      </section>

      {/* Company Introduction */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
            <div className="lg:col-span-5">
              <div className="inline-block border border-gray-300 px-4 py-1 rounded-full text-xs text-gray-600 mb-4">
                ABOUT US
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-primary mb-2">
                おかやま解体工事株式会社
              </h2>
              <p className="text-xl text-gray-500 mb-12">Okayama Kaitai Construction</p>
              <div className="space-y-6 text-gray-700 text-lg leading-relaxed mb-12">
                <p>
                  <strong>おかやま解体工事株式会社</strong>は、1993年の創業以来、岡山市を中心に建物解体工事、内装解体工事、造園工事、外構エクステリア工事、不用品処理など、幅広いサービスを提供してまいりました。
                </p>
                <p>
                  創業30年以上の経験と実績を活かし、お客様一人ひとりのニーズに合わせた最適なソリューションをご提案いたします。安全管理を最優先に、近隣への配慮を徹底し、地域社会に貢献できる企業を目指しています。
                </p>
                <p>
                  最新の重機と熟練の技術を持つスタッフが、迅速かつ丁寧に作業を進めます。環境にも配慮し、廃材の適切な分別処理を行い、持続可能な社会の実現に貢献しています。
                </p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-2xl overflow-hidden shadow-xl h-[500px]">
                <img
                  src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/d49417bcf32190a0af62da65f0ed0e4a.jpeg"
                  alt="会社紹介"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Information Table */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <h2 className="text-4xl lg:text-5xl font-bold text-primary text-center mb-12">会社情報</h2>
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
            {companyInfo.map((item, index) => (
              <div
                key={index}
                className={`grid grid-cols-1 md:grid-cols-3 gap-4 p-6 ${
                  index !== companyInfo.length - 1 ? 'border-b border-gray-200' : ''
                }`}
              >
                <div className="text-gray-600 font-medium md:text-right">{item.label}</div>
                <div className="md:col-span-2 text-primary font-medium text-lg">{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <h2 className="text-4xl lg:text-5xl font-bold text-primary text-center mb-12">
            保有資格・許可
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications.map((cert, index) => (
              <div
                key={index}
                className="bg-white border-2 border-gray-200 rounded-xl p-8 hover:shadow-lg transition-shadow"
              >
                <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mb-6 mx-auto">
                  <i className={`${cert.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-primary font-bold text-xl text-center mb-3">{cert.title}</h3>
                <p className="text-gray-600 text-sm text-center leading-relaxed">{cert.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Access Map */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <h2 className="text-4xl lg:text-5xl font-bold text-primary text-center mb-12">アクセス</h2>
          <div className="max-w-5xl mx-auto">
            <div className="h-96 lg:h-[450px] rounded-xl overflow-hidden mb-8">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d52634.64977654!2d133.89!3d34.66!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzTCsDM5JzQyLjEiTiAxMzPCsDU2JzA2LjAiRQ!5e0!3m2!1sja!2sjp!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="おかやま解体工事株式会社 所在地"
              ></iframe>
            </div>
            <div className="bg-white rounded-xl shadow-md p-8 text-center max-w-2xl mx-auto">
              <address className="text-primary text-lg not-italic mb-4">
                〒700-0000<br />
                岡山市北区○○町1-2-3
              </address>
              <a
                href="https://www.google.com/maps/search/?api=1&query=34.6617,133.9350"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-primary underline hover:text-secondary transition-colors cursor-pointer"
              >
                Googleマップで開く
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/c0f0e12795c352d552f6520acf0146f1.jpg"
            alt="岡山の風景"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight">
              まずはお気軽に<br />ご相談ください
            </h2>
          </div>
          <div className="absolute bottom-12 right-8 text-right">
            <p className="text-white text-xl lg:text-2xl font-light mb-4">
              無料見積もり・現地調査実施中
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-end">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary rounded-full hover:bg-gray-100 transition-all font-medium whitespace-nowrap cursor-pointer"
              >
                お問い合わせ
              </Link>
              <a
                href="tel:086-012-3456"
                className="inline-flex items-center justify-center px-8 py-4 bg-secondary text-white rounded-full hover:bg-primary transition-all font-medium whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                086-012-3456
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CompanyPage;
